import { createContext, useContext, useEffect, useRef } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { createSocket } from "@/lib/socket";
import { Bell, TrendUp, Clock, Tag } from "@phosphor-icons/react";

const Ctx = createContext(null);

// Small chime encoded as base64 WAV. ~0.2s soft "ding".
const CHIME_SRC =
  "data:audio/wav;base64,UklGRl9vAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YT9v" +
  "AACAgH+Af4B/gH+Af4B/gH+Af4B/gH+A"; // minimal fallback (silent). We'll use WebAudio API below instead.

function playBeep() {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(660, ctx.currentTime + 0.15);
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.2, ctx.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.35);
    osc.connect(gain).connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.36);
    setTimeout(() => ctx.close && ctx.close(), 500);
  } catch (e) {
    // Ignore
  }
}

function browserNotify(title, body) {
  if (typeof window === "undefined" || !("Notification" in window)) return;
  if (Notification.permission === "granted") {
    try { new Notification(title, { body, icon: "/favicon.ico" }); } catch (e) { /* ignore */ }
  }
}

export function NotificationProvider({ children }) {
  const { user } = useAuth();
  const socketRef = useRef(null);

  // Ask for browser notification permission once user is logged in
  useEffect(() => {
    if (!user) return;
    if (typeof window === "undefined" || !("Notification" in window)) return;
    if (Notification.permission === "default") {
      // Delay a bit so it's not intrusive on load
      const t = setTimeout(() => Notification.requestPermission().catch(() => {}), 3000);
      return () => clearTimeout(t);
    }
  }, [user]);

  useEffect(() => {
    if (!user) {
      if (socketRef.current?.connected) socketRef.current.disconnect();
      return;
    }
    const s = createSocket();
    socketRef.current = s;
    s.on("connect", () => s.emit("join_user", { user_id: user.user_id }));

    const push = (event, opts) => {
      s.on(event, (data) => {
        const { title, body, icon } = opts.render(data);
        toast(title, {
          description: body,
          icon: opts.icon,
          duration: 6000,
          action: data.auction_id ? {
            label: "Aç",
            onClick: () => { window.location.href = `/mezat/${data.auction_id}`; },
          } : undefined,
        });
        playBeep();
        browserNotify(title, body);
      });
    };

    push("favorite_new_bid", {
      icon: "💰",
      render: (d) => ({
        title: "Favorinize yeni teklif geldi",
        body: `${d.title} · ₺${Number(d.amount).toLocaleString("tr-TR")} · ${d.bidder_name}`,
      }),
    });
    push("favorite_ending_soon", {
      icon: "⏱️",
      render: (d) => ({
        title: "Son 10 dakika!",
        body: `Favorinizdeki "${d.title}" birazdan sona eriyor.`,
      }),
    });
    push("favorite_price_drop", {
      icon: "🏷️",
      render: (d) => ({
        title: "Favorinizin fiyatı düştü",
        body: `${d.title} için satıcı fiyatı güncelledi.`,
      }),
    });
    // Also route existing personal events (outbid, offers, questions) — but silently, no sound
    ["new_offer", "offer_accepted", "offer_rejected", "question_asked", "question_answered", "store_new_listing"].forEach((ev) => {
      s.on(ev, () => {
        // Silent — panel bildirimleri açtığında görecek
      });
    });

    return () => { if (s.connected) s.disconnect(); };
  }, [user?.user_id]); // eslint-disable-line

  return <Ctx.Provider value={{ socket: socketRef }}>{children}</Ctx.Provider>;
}

export const useNotifications = () => useContext(Ctx);
